import * as ng from 'angular';
import * as _ from 'underscore';

interface Column {
   isMapped:boolean;
   name:string;
   mergeVar:string;
}

export class AdhocEmailController {
   templates:{}[];

   page = 1;
   limit = 20;
   alert:string;
   error?:'invalidCsv'|'emptyCsv'|'noEmailColumn';
   csvIsLoaded:boolean;
   deployName:string;

   columns:Column[];
   records:{}[];
   recordsInPage:{}[];

   private _template:{mergeVars:string[], id:string, name:string};

   get template() { return this._template; }
   set template(v) {
      this._template = v;
      if (v) {
         this.autoMapColumns();
      }
   }

   static $inject = ['emailApi', 'session', 'dialogs', 'toast', 'loader', 'titleService'];

   constructor (private emailApi, private session, private dialogs, private toast, private loader, titleService) {
      titleService.setTitle('Ad-Hoc Emails');

      loader.showMessage('Email templates are being loaded', 'This may take a few moments...');
      emailApi.listTemplates().then((templates) => {
         this.templates = templates;
         loader.hide();
      });
   }

   private applyPaging() {
      let offset = (this.page - 1) * this.limit;
      let end = Math.min(this.records.length, offset + this.limit);
      this.recordsInPage = this.records.slice(offset, end);
   }

   private send() {
      let model = {
         campaignId: this.session.getCampaign().id,
         templateName: this.template.id,
         recipients: this.records,
         deployName: this.deployName,
         mergeVarMap: _.chain(this.columns)
            .filter((c) => c.isMapped)
            .map((c) => [c.mergeVar, c.name])
            .object()
            .value()
      };

      this.loader.showSpinner();
      this.emailApi.sendBulk(model).then(() => {
         this.reset();
         this.toast.success()
            .message('The emails have been sent')
            .send();
         this.loader.hide();
      });
   }

   private reset() {
      this.error = null;
      this.csvIsLoaded = false;
      this.records = null;
      this.columns = null;
   }

   private getUnmapped() {
      return _.filter(this.template.mergeVars, (v) => {
         return !_.some(this.columns, (c) => {
            return c.mergeVar == v;
         });
      });
   }

   private autoMapColumns() {
      _.each(this.columns, (c) => {
         let mergeVar = _.find(this.template.mergeVars, (i) => {
            return i.toLowerCase() === c.name.toLowerCase();
         });

         if (mergeVar) {
            c.isMapped = true;
            c.mergeVar = mergeVar;
         } else {
            c.isMapped = false;
            c.mergeVar = null;
         }
      });

      this.updateMappingAlert();
   }

   private updateMappingAlert() {
      let unmapped = this.getUnmapped();

      if (unmapped.length == 1) {
         this.alert = unmapped[0] + ' is unmapped';
      } else if (unmapped.length > 0) {
         this.alert = unmapped.join(', ') + ' are unmapped';
      } else {
         delete this.alert;
      }
   }

   uploadCsv(csvFile) {
      this.reset();

      // hitting cancel in file picker results in "undefined" file
      if (!csvFile) {
         return;
      }

      this.emailApi.csvToJson(csvFile).then((response) => {
         let records = response.data;
         if (records.length === 0) {
            this.error = 'emptyCsv';
         } else {
            let columns = _.map<any, Column>(records[0], (val, key:string) => {
               return {
                  name: key,
                  isMapped: false,
                  mergeVar: null
               };
            });

            let emailColumn = _.find(columns, (col) => col.name.toLowerCase() == 'email');

            if (!emailColumn) {
               this.error = 'noEmailColumn';
            } else {
               this.records = records;
               this.columns = columns;
               this.applyPaging();

               if (this.template) {
                  this.autoMapColumns();
               }
            }
         }
      }).catch(() => {
         this.error = 'invalidCsv';
      });
   }

   mapColumn(column, mergeVar) {
      _.each(this.columns, (c) => {
         if (c.mergeVar == mergeVar) {
            c.isMapped = false;
            c.mergeVar = mergeVar;
         }
      });

      column.mergeVar = mergeVar;
      column.isMapped = true;

      this.updateMappingAlert();
   }

   submit() {
      if (!this.template) {
         this.toast.error()
            .message('You must select a template.')
            .send();
      } else if (this.getUnmapped().length > 0) {
         this.toast.error()
            .message('All merge variables must be mapped to columns.')
            .send();
      } else {
         this.dialogs.confirm('Are you sure you want to send emails?').then(() => {
            this.send();
         });
      }
   }

   setPage(page) {
      this.page = page;
      if (this.records) {
         this.applyPaging();
      }
   }
}

ng.module('e360')
   .controller('AdhocEmailController', AdhocEmailController);
